/**
 * Agent Form Dialog Component
 * Reusable dialog for creating/editing agents
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Box,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import { agentApi, embeddingModelApi, llmModelApi } from '../../services/api';
import type {
  AgentListItem,
  AgentCreate,
  AgentType,
  AgentUpdate,
  EmbeddingModel,
  LlmModel,
} from '../../types';
import { useAuth } from '../../context/AuthContext';
import { LlmModelAutocomplete } from '../common/AsyncEntitySelect';
import { PromptEditor } from '../common/PromptEditor';

const AGENT_TYPE_OPTIONS: Array<{ value: AgentType; label: string; staffOnly?: boolean }> = [
  { value: 'ASSISTANT', label: 'Agent' },
  { value: 'AI_SYSTEM_BUILDER', label: 'AI System Builder' },
  { value: 'MAIN_ASSISTANT', label: 'Main Agent' },
  { value: 'KNOWLEDGE_GRAPH', label: 'Knowledge Graph (admin)', staffOnly: true },
  { value: 'BOT_BUILDER', label: 'Bot Builder' },
  { value: 'MODEL_LAB_BUILDER', label: 'Model Lab Builder (admin)', staffOnly: true },
];

const getAgentTypeLabel = (agentType?: AgentType) =>
  AGENT_TYPE_OPTIONS.find((option) => option.value === agentType)?.label.replace(' (admin)', '') || 'Agent';

export interface AgentFormDialogProps {
  open: boolean;
  agent?: AgentListItem | null;
  models?: LlmModel[];
  onClose: () => void;
  onSuccess?: (agent?: any) => void;
  /** When true, only show view mode (read-only) */
  viewOnly?: boolean;
}

export const AgentFormDialog: React.FC<AgentFormDialogProps> = ({
  open,
  agent,
  models: externalModels,
  onClose,
  onSuccess,
  viewOnly = false,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState<AgentCreate | AgentUpdate>({
    name: '',
    description: '',
    system_prompt: '',
    model_id: 0,
    stt_model_id: undefined,
    tts_model_id: undefined,
    embedding_model_id: undefined,
    agent_type: 'ASSISTANT',
    capabilities: [],
    is_active: true,
    is_default: false,
    is_template: false,
    template_source_id: undefined,
  });
  const [templates, setTemplates] = useState<AgentListItem[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [models, setModels] = useState<LlmModel[]>(externalModels || []);
  const [embeddingModels, setEmbeddingModels] = useState<EmbeddingModel[]>([]);

  // Load models if not provided
  useEffect(() => {
    if (!externalModels) {
      llmModelApi.list({}).then((data) => {
        setModels(data?.results || []);
      }).catch(console.error);
    } else {
      setModels(externalModels);
    }
  }, [externalModels]);

  useEffect(() => {
    embeddingModelApi.list({ is_active: true }).then((data) => {
      setEmbeddingModels(data?.results || []);
    }).catch(console.error);
  }, []);

  // Load templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const templateList = await agentApi.listTemplates();
        setTemplates(templateList);
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, []);

  // Initialize form when agent changes
  useEffect(() => {
    if (agent) {
      // Fetch full agent details if we have an agent ID
      if (agent.id) {
        agentApi.get(agent.id).then((fullAgent) => {
          setFormData({
            name: fullAgent.name,
            description: fullAgent.description || '',
            system_prompt: fullAgent.system_prompt || '',
            model_id: fullAgent.model?.id || fullAgent.model_id || 0,
            stt_model_id: fullAgent.stt_model?.id || fullAgent.stt_model_id || undefined,
            tts_model_id: fullAgent.tts_model?.id || fullAgent.tts_model_id || undefined,
            embedding_model_id:
              fullAgent.embedding_model?.id ||
              fullAgent.embedding_model_id ||
              undefined,
            agent_type: fullAgent.agent_type || 'ASSISTANT',
            is_active: fullAgent.is_active,
            is_default: Boolean(fullAgent.is_default),
            is_template: fullAgent.is_template || false,
            template_source_id: undefined,
          });
        }).catch(console.error);
      } else {
        setFormData({
          name: agent.name,
          description: agent.description || '',
          system_prompt: '',
          model_id: 0,
          stt_model_id: undefined,
          tts_model_id: undefined,
          embedding_model_id: undefined,
            agent_type: agent.agent_type || 'ASSISTANT',
            is_active: agent.is_active,
            is_default: Boolean(agent.is_default),
            template_source_id: undefined,
        });
      }
    } else {
      setFormData({
        name: '',
        description: '',
        system_prompt: 'You are a helpful AI agent.',
        model_id: models[0]?.id || 0,
        stt_model_id: undefined,
        tts_model_id: undefined,
        embedding_model_id: undefined,
        agent_type: 'ASSISTANT',
        capabilities: [],
        is_active: true,
        is_default: false,
        template_source_id: undefined,
      });
    }
  }, [agent, models, open]);

  // Handle template selection
  const handleTemplateChange = (templateId: number | '') => {
    if (templateId === '') {
      setFormData({ 
        ...formData, 
        template_source_id: undefined,
        embedding_model_id: formData.embedding_model_id,
        agent_type: 'ASSISTANT',
      });
    } else {
      const selectedTemplate = templates.find(t => t.id === templateId);
      setFormData({ 
        ...formData, 
        template_source_id: templateId,
        agent_type: selectedTemplate?.agent_type || 'ASSISTANT',
      });
    }
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: AgentCreate) => agentApi.create(data),
    onSuccess: (newAgent) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents-for-expert'] });
      queryClient.invalidateQueries({ queryKey: ['agent-selector'] });
      toast.success('Agent created successfully');
      onSuccess?.(newAgent);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AgentUpdate }) =>
      agentApi.update(id, data),
    onSuccess: (updatedAgent) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents-for-expert'] });
      queryClient.invalidateQueries({ queryKey: ['agent-selector'] });
      queryClient.invalidateQueries({ queryKey: ['agent', agent?.id] });
      toast.success('Agent updated successfully');
      onSuccess?.(updatedAgent);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    },
  });

  const handleSubmit = () => {
    const payload = {
      ...formData,
      is_default: Boolean(formData.is_default) && Boolean(formData.is_active),
    };
    if (agent?.id) {
      updateMutation.mutate({ id: agent.id, data: payload });
    } else {
      createMutation.mutate(payload as AgentCreate);
    }
  };

  const isEditing = !!agent?.id;
  const title = viewOnly 
    ? `Agent: ${formData.name}` 
    : (isEditing ? 'Edit Agent' : 'Create New Agent');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
            disabled={viewOnly}
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
            disabled={viewOnly}
          />

          {/* Template Selection Dropdown - Only show when creating (not editing) */}
          {!isEditing && !viewOnly && (
            <FormControl fullWidth>
              <InputLabel>Provision from Template (Optional)</InputLabel>
              <Select
                value={formData.template_source_id || ''}
                label="Provision from Template (Optional)"
                onChange={(e) => handleTemplateChange(e.target.value as number | '')}
                disabled={loadingTemplates}
              >
                <MenuItem value="">
                  <em>None - Create from scratch</em>
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} ({getAgentTypeLabel(template.agent_type)})
                    {template.owner_name ? (
                      <em style={{ marginLeft: 8, color: '#666' }}> — {template.owner_name}</em>
                    ) : null}
                  </MenuItem>
                ))}
              </Select>
              {formData.template_source_id && (
                <Typography variant="caption" color="info.main" sx={{ mt: 0.5 }}>
                  Agent will be provisioned from the selected template with pre-configured settings.
                </Typography>
              )}
            </FormControl>
          )}

          {/* Hide System Prompt when template is selected */}
          {!formData.template_source_id && (
            <Box>
              <PromptEditor
                label={`System Prompt ${!viewOnly ? '*' : ''}`}
                value={formData.system_prompt || ''}
                onChange={(value) =>
                  !viewOnly && setFormData({ ...formData, system_prompt: value || '' })
                }
                helperText="Define the agent's role and behavior"
                disabled={viewOnly}
                minHeight={300}
                showInputs={false}
                showSnippets
                defaultShowSnippetPanel={!viewOnly}
              />
            </Box>
          )}

          {/* Hide Agent Type when template is selected */}
          {!formData.template_source_id && (
            <FormControl fullWidth required disabled={viewOnly}>
              <InputLabel>Agent Type</InputLabel>
              <Select
                value={formData.agent_type}
                label="Agent Type"
                onChange={(e) => setFormData({ ...formData, agent_type: e.target.value as AgentType })}
              >
                {AGENT_TYPE_OPTIONS.filter(
                  (option) => !option.staffOnly || user?.is_staff || formData.agent_type === option.value
                ).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Hide LLM Model when template is selected */}
          {!formData.template_source_id && (
            <LlmModelAutocomplete
              value={formData.model_id || null}
              onChange={(modelId) => setFormData({ ...formData, model_id: modelId || 0 })}
              label="LLM Model"
              required
              disabled={viewOnly}
              size="medium"
              filters={{ is_active: true }}
            />
          )}

          {!formData.template_source_id && (
            <LlmModelAutocomplete
              value={formData.stt_model_id || null}
              onChange={(modelId) => setFormData({ ...formData, stt_model_id: modelId ?? null })}
              label="STT Model"
              helperText="Used for voice messages when the chat model cannot consume audio directly."
              disabled={viewOnly}
              size="medium"
              filters={{ is_active: true, transcription_supported: true }}
            />
          )}

          {!formData.template_source_id && (
            <LlmModelAutocomplete
              value={formData.tts_model_id || null}
              onChange={(modelId) => setFormData({ ...formData, tts_model_id: modelId ?? null })}
              label="TTS Model"
              helperText="Used to synthesize assistant voice output."
              disabled={viewOnly}
              size="medium"
              filters={{ is_active: true, speech_synthesis_supported: true }}
            />
          )}

          {!formData.template_source_id && (
            <FormControl fullWidth disabled={viewOnly}>
              <InputLabel>Embedding Model</InputLabel>
              <Select<number | ''>
                value={formData.embedding_model_id ?? ''}
                label="Embedding Model"
                onChange={(e) => setFormData({
                  ...formData,
                  embedding_model_id: e.target.value === ''
                    ? undefined
                    : e.target.value as number,
                })}
              >
                <MenuItem value="">
                  <em>Use provider or agent defaults</em>
                </MenuItem>
                {embeddingModels.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name} ({model.provider_name})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                disabled={viewOnly}
              />
            }
            label="Active"
          />

          <FormControlLabel
            control={
              <Switch
                checked={Boolean(formData.is_default) && Boolean(formData.is_active)}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                disabled={viewOnly || !formData.is_active}
              />
            }
            label={
              formData.is_template
                ? 'Default template for this agent type'
                : 'Default for this agent type'
            }
          />

          {/* Hide is_template toggle when template is selected */}
          {user?.is_staff && !formData.template_source_id && !viewOnly && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_template || false}
                  onChange={(e) => setFormData({ ...formData, is_template: e.target.checked })}
                  color="warning"
                />
              }
              label="Template (Admin Only)"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{viewOnly ? 'Close' : 'Cancel'}</Button>
        {!viewOnly && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AgentFormDialog;
